// src/portals/teacher/MyEnvironment.tsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaCalendarCheck, FaChild, FaClipboardList, FaIdCard, FaUserGraduate } from "react-icons/fa";
import http from "../../api/apiClient";
import { useCan } from "../../authz/useCan";
import {
  LdsOverviewCard,
  LdsOverviewEmpty,
  LdsOverviewGrid,
  LdsOverviewHero,
  LdsOverviewPage,
  LdsOverviewSection,
} from "../../components/lds/LdsOverview";
import { getClassroomId, listMyClassrooms } from "../../services/myEnvironmentService";
import { API_PATH as SCHOOL_CALENDAR_API_PATH } from "../../pages/schoolCalendar/constants";
import {
  eventClassroomUuids,
  eventScope,
  formatDateOnly,
  normalizeList,
} from "../../pages/schoolCalendar/utils";
import type { SchoolCalendarEvent } from "../../pages/schoolCalendar/types";
import DashboardQuickSearch from "../../pages/Dashboard/components/DashboardQuickSearch";
import TeacherLinkSetupModal from "./TeacherLinkSetupModal";

type DashboardActivity = {
  id: string;
  title: string;
  startDate: unknown;
};

const myEnvironmentStyles = `
  @keyframes myEnvironmentActivityEnter {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .my-environment-activity-enter {
    animation: myEnvironmentActivityEnter 360ms cubic-bezier(0.22, 1, 0.36, 1);
    animation-fill-mode: both;
  }

  @media (prefers-reduced-motion: reduce) {
    .my-environment-activity-enter {
      animation: none !important;
    }
  }
`;

const toActivityMillis = (value: unknown) => {
  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day, hour = 0, minute = 0, second = 0] = value.map(Number);
    return new Date(year, month - 1, day, hour, minute, second).getTime();
  }

  if (typeof value === "number") {
    const millis = value < 1_000_000_000_000 ? value * 1000 : value;
    return new Date(millis).getTime();
  }

  if (typeof value === "string" && value.trim()) {
    return new Date(value).getTime();
  }

  return Number.NaN;
};

const MyEnvironment: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const canViewMyClassrooms = useCan("my.classrooms", "view");
  const canViewMyDetails = useCan("my.teacher-details", "view");
  const canViewMyParentMeetings = useCan("my.parent-meetings", "view");
  const canViewMyAgendaActivities = useCan("my.agenda-activities", "view");
  const canReadClassroomCalendar = useCan("classroom.calendar", "read");
  const canManageClassroomCalendar = useCan("classroom.calendar", "manage");
  const canReadOutings = useCan("outing.info", "read");
  const canManageOutings = useCan("outing.info", "manage");
  const canViewOutingsReport = useCan("reports.outings", "view");

  const pageTitle = t("myEnvironment.title", { defaultValue: "My environment" });
  const [upcomingActivities, setUpcomingActivities] = useState<DashboardActivity[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const canLoadCalendar = canReadClassroomCalendar || canManageClassroomCalendar;
  const canViewAgendaActivitiesPage =
    canViewMyAgendaActivities ||
    canLoadCalendar ||
    canReadOutings ||
    canManageOutings ||
    canViewOutingsReport;

  const cards = [
    {
      eyebrow: t("myEnvironment.classrooms.eyebrow", { defaultValue: "Classes" }),
      title: t("myEnvironment.classrooms.title", { defaultValue: "My classrooms" }),
      description: t("myEnvironment.classrooms.description", {
        defaultValue: "View your own classrooms and pupils.",
      }),
      icon: FaChild,
      path: "/my/classrooms",
      visible: canViewMyClassrooms,
    },
    {
      eyebrow: t("myEnvironment.parentMeetings.eyebrow", { defaultValue: "Planning" }),
      title: t("myEnvironment.parentMeetings.title", { defaultValue: "Parent meetings" }),
      description: t("myEnvironment.parentMeetings.description", {
        defaultValue: "View open slots, reservations and confirmed meetings.",
      }),
      icon: FaCalendarCheck,
      path: "/my/parent-meetings",
      visible: canViewMyParentMeetings,
    },
    {
      eyebrow: t("myEnvironment.outings.eyebrow", { defaultValue: "Activities" }),
      title: t("myEnvironment.outings.title", { defaultValue: "Agenda & activities" }),
      description: t("myEnvironment.outings.description", {
        defaultValue: "View calendar items, activities, outings and parent consent status.",
      }),
      icon: FaClipboardList,
      path: "/my/outings",
      visible: canViewAgendaActivitiesPage,
    },
    {
      eyebrow: t("myEnvironment.teacherDetails.eyebrow", { defaultValue: "Profile" }),
      title: t("myEnvironment.teacherDetails.title", { defaultValue: "My details" }),
      description: t("myEnvironment.teacherDetails.description", { defaultValue: "View and manage your personal teacher details." }),
      icon: FaIdCard,
      path: "/my/details",
      visible: canViewMyDetails,
    },
  ].filter((card) => card.visible);

  useEffect(() => {
    if (!canLoadCalendar) {
      setUpcomingActivities([]);
      return;
    }

    let cancelled = false;

    const loadDashboardActivities = async () => {
      try {
        setActivitiesLoading(true);
        const classrooms = await listMyClassrooms();
        const classroomIds = new Set(classrooms.map(getClassroomId).filter(Boolean).map(String));

        if (!classroomIds.size) {
          if (!cancelled) setUpcomingActivities([]);
          return;
        }

        const { data } = await http.get(SCHOOL_CALENDAR_API_PATH);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const activities = normalizeList<SchoolCalendarEvent>(data)
          .filter((event) => eventScope(event) === "CLASSROOM")
          .filter((event) => eventClassroomUuids(event).some((id) => classroomIds.has(String(id))))
          .map((event) => ({
            id: String(event.uuid ?? event.id ?? event.eventId ?? `${event.title || "activity"}-${event.startDate || ""}`),
            title: event.title || "",
            startDate: event.startDate,
            time: toActivityMillis(event.startDate),
          }))
          .filter((event) => Number.isFinite(event.time) && event.time >= today.getTime())
          .sort((a, b) => a.time - b.time)
          .slice(0, 3)
          .map(({ time, ...event }) => event);

        if (!cancelled) setUpcomingActivities(activities);
      } catch {
        if (!cancelled) setUpcomingActivities([]);
      } finally {
        if (!cancelled) setActivitiesLoading(false);
      }
    };

    loadDashboardActivities();

    return () => {
      cancelled = true;
    };
  }, [canLoadCalendar]);

  return (
    <LdsOverviewPage styles={<style>{myEnvironmentStyles}</style>}>
      <TeacherLinkSetupModal />

      <LdsOverviewHero
        icon={FaUserGraduate}
        eyebrow={t("myEnvironment.eyebrow", { defaultValue: "Personal workspace" })}
        title={pageTitle}
        description={t("myEnvironment.dashboard.description", {
          defaultValue: "Upcoming activities, parent meetings and events for your classrooms.",
        })}
      />

      {/* Same quick search as on the dashboard; it only renders with parent and child list access. */}
      <DashboardQuickSearch />

      <LdsOverviewSection
        title={t("myEnvironment.dashboard.title", { defaultValue: "For you" })}
        description={t("myEnvironment.dashboard.upcoming", { defaultValue: "Upcoming" })}
      >
        <div className="rounded-[28px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_14px_35px_rgba(15,23,42,0.07)] dark:border-slate-800 dark:bg-slate-950">
          <div className="space-y-3">
            {activitiesLoading ? (
              <p className="my-environment-activity-enter rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
                {t("common.loading", { defaultValue: "Loading..." })}
              </p>
            ) : upcomingActivities.length ? (
              upcomingActivities.map((activity, index) => (
                <div
                  key={activity.id}
                  className="my-environment-activity-enter flex gap-3 rounded-2xl border border-slate-100 bg-slate-50/80 px-4 py-3 dark:border-white/10 dark:bg-white/5"
                  style={{ animationDelay: `${80 + index * 60}ms` }}
                >
                  <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-indigo-500" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                      {activity.title || t("schoolCalendar.untitled", { defaultValue: "Untitled" })}
                    </p>
                    <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
                      {formatDateOnly(activity.startDate, i18n.language)}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="my-environment-activity-enter rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-3 text-sm text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
                {t("myEnvironment.dashboard.empty", {
                  defaultValue: "There are currently no upcoming activities.",
                })}
              </p>
            )}
          </div>
        </div>
      </LdsOverviewSection>

      {cards.length ? (
        <LdsOverviewGrid className="md:grid-cols-3">
          {cards.map((card, index) => (
            <LdsOverviewCard
              key={card.path}
              icon={card.icon}
              eyebrow={card.eyebrow}
              title={card.title}
              description={card.description}
              actionLabel={t("common.open", { defaultValue: "Open" })}
              animationIndex={index}
              onClick={() => {
                void navigate(card.path);
              }}
            />
          ))}
        </LdsOverviewGrid>
      ) : (
        <LdsOverviewEmpty>
          {t("myEnvironment.empty", {
            defaultValue: "No personal workspace sections are available for your account.",
          })}
        </LdsOverviewEmpty>
      )}
    </LdsOverviewPage>
  );
};

export default MyEnvironment;
